import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import sanitizer from 'sanitizer';
import cn from 'classnames';

import Feedback from 'images/zappos/feedback.svg';
import Sort from 'components/search/Sort';
import NewSort from 'components/search/NewSort';
import { retrieveH1TagFromFilters } from 'helpers/SeoOptimizedDataHelper';
import marketplace from 'cfg/marketplace.json';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { track } from 'apis/amethyst';
import { SEARCH_PAGE } from 'constants/amethystPageTypes';
import { evToggleDesktopFacets } from 'events/search';
import FeatureFeedback from 'components/FeatureFeedback';
import Tooltip from 'components/common/Tooltip';
import useFocusTrap from 'hooks/useFocusTrap';

import css from 'styles/components/search/desktopSearchHeader.scss';

const {
  search: { hasSelectSort, sortOptions, showSearchFeedback, hasDesktopFacetToggle }
} = marketplace;

export const DesktopSearchHeader = ({ totalProductCount, onSortSelected, filters, makePersonalizedSortToggle, hydraNewProductCard, onToggleFacetsContainer, isFacetsVisible, hydraPills, hydraNewSearchNav, hydraSearchAutoCorrect }, { testId }) => {
  const [ tooltipVisible, setTooltipVisible] = useState(false);
  const { sort, originalTerm, term, autocorrect, honeTag } = filters;
  const SortComponent = hydraNewSearchNav ? NewSort : Sort;

  const headerProps = {
    'className': css.heading,
    'data-test-id': testId('desktopSearchResultsText')
  };

  let subHeadingText = !!totalProductCount && `${totalProductCount} items found`;

  let pageMainHeader = <h1
    {...headerProps}
    dangerouslySetInnerHTML={{
      '__html': sanitizer.sanitize(retrieveH1TagFromFilters(filters))
    }}/>;

  if (hydraSearchAutoCorrect) {
    const displayedTerm = originalTerm || term;
    const termMessage = displayedTerm ? <> for "<strong>{honeTag}</strong>"</> : '';
    const headingContent = <>We found {totalProductCount} items{termMessage}</>;

    pageMainHeader = <h1 {...headerProps}>{headingContent}</h1>;
    subHeadingText = autocorrect.termBeforeAutocorrect ? (
      <>
        We couldn't find anything for "<strong>{autocorrect.termBeforeAutocorrect}</strong>"
      </>
    ) : '';
  }

  useEffect(() => {
    // original term Changed
    setTooltipVisible(false);
  }, [originalTerm]);

  const tooltipClick = () => {
    const element = document.getElementById('feedbackRequest');
    if (element.getAttribute('aria-expanded') === 'true') {
      element.setAttribute('aria-expanded', 'false');
    } else {
      element.setAttribute('aria-expanded', 'true');
    }
    setTooltipVisible(tooltipVisible ? false : true);
  };

  const closeFeedback = () => {
    setTooltipVisible(false);
  };

  const handleKeydown = useCallback(e => {
    if (e.key === 'Escape' || e.key === 'Esc') {
      closeFeedback();
    }
  }, []);

  const desktopToggleFacets = () => {
    // only want amethyst events for this button, not everytime the method is triggered.
    // If hydra nsp passes this can be combined with the toggle in StandardSearch and all events triggered there.
    track(() => ([
      evToggleDesktopFacets,
      !isFacetsVisible
    ]));
    onToggleFacetsContainer();
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [handleKeydown]);

  const tooltipRef = useFocusTrap({ shouldFocusFirstElement: true, active: tooltipVisible });
  const filterButtonText = isFacetsVisible ? 'Show Filters' : 'Hide Filters';

  function makeSearchFeedback() {
    return (
      <div ref={tooltipRef}>
        <Tooltip
          tooltipId="searchFeedback"
          tooltipClassName={css.tooltip}
          contentClassName={css.content}
          mobileWrapper={css.mobileContainer}
          mobileClass={css.mobileEncompasss}
          direction="right"
          clickable={true}
          clickOpen={tooltipVisible}
          content={
          <>
            <header>
              {/* following eslint-disable is for accessibility */}
              <h3 // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
                id="feedBackTitle">Let us know your feedback!</h3>
              <button
                type="button"
                value="close"
                className={css.close}
                aria-label="Close"
                onClick={() => closeFeedback()}>
              </button>
            </header>
            <FeatureFeedback
              isYesNoOnly={true}
              autoOpenOnYesNoClick={true}
              completionMessage="Thank you!"
              feedbackQuestion="Did you find what you were looking for?"
              feedbackType="SEARCH_RELEVANCY_FEEDBACK"
              pageType={SEARCH_PAGE}
              source="search"
              wrapperClass={css.feedbackWrapper}
              yesNoWrapperClass={css.feedbackYesNoWrapper}
            />
          </>
          }>
        </Tooltip>
      </div>);
  }

  return (
    <div className={css.desktopSearchHeader}>
      <div className={cn(css.countWrapper, { [css.autocorrect]: hydraSearchAutoCorrect })}>
        {pageMainHeader}
        <span className={css.subheading} data-test-id={testId('searchResultsItemCount')}>
          {subHeadingText}
        </span>
        {!!hydraNewProductCard && showSearchFeedback &&
        <section className={css.searchFeedback}>
          <button
            type="button"
            className={css.feedbackButton}
            onClick={tooltipClick}
            aria-expanded="false"
            id="feedbackRequest">
              Have Feedback?
            <img src={Feedback} alt="" />
          </button>
          {makeSearchFeedback()}
        </section>
        }
      </div>
      {hasSelectSort && <span className={css.sortWrapper} data-test-id={testId('desktopSearchSort')}>
        <div className={css.topRight}>
          {hydraPills && hasDesktopFacetToggle && <button
            className={css.hideFacetsButton}
            type="button"
            onClick={desktopToggleFacets}
            data-test-id={testId('hideFacetButton')}>{filterButtonText}</button>}
          <SortComponent
            tabIndex={0}
            sortOptions={sortOptions}
            onSortSelected={onSortSelected}
            selectedSort={sort}
            filters={filters}
            testId={testId('selectSortValue')} />
        </div>
        {makePersonalizedSortToggle && makePersonalizedSortToggle()}
      </span>}
    </div>
  );
};

DesktopSearchHeader.displayName = 'DesktopSearchHeader';
DesktopSearchHeader.contextTypes = {
  testId: PropTypes.func
};

export default withErrorBoundary('DesktopSearchHeader', DesktopSearchHeader);
